import React from 'react';
import * as Yup from 'yup';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, type FieldValues, type UseFormReturn } from 'react-hook-form';

import type { AxiosError } from 'axios';
import { updateOrgProfile } from '../../../../accounts/AccountService';
import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';
import { useMailingAddressValidationSchema } from '../../../../console/agencies/quotes/PrimeX/AccountDetails/forms/useAccountInfoFormFieldValidationSchema';
import { useCountryNameByPlatform } from '../../../../console/agencies/quotes/PrimeX/AccountDetails/helpers/submission-firmo-utils';
import useMailingAddressSync from '../../../../console/submissions/cowbell/quotes/hooks/useMailingAddressFormSync';
import { useLanguageFormState } from '../../../../i18n/forms/LanguageForm';
import { isUnitedStates } from '../../../../i18n/country';
import {
  COUNTRY_MAPPING,
  type Countries,
  type Languages,
} from '../../../../i18n/i18n.language-config';
import { manageAPIError } from '../../../../utils';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { countMissingRequired } from './countMissingRequired';
import { useSettledDirty } from './useSettledDirty';
import { defaultCountryCodeFor } from './org-phone-format';

export interface OrgAddressOrgData {
  readonly state?: string;
  readonly address1?: string;
  readonly address2?: string;
  readonly city?: string;
  readonly zipCode?: string;
  readonly phoneNumber?: number;
  readonly country?: string;
  readonly mailingAddress?: {
    readonly address1?: string;
    readonly address2?: string;
    readonly address3?: string;
    readonly city?: string;
    readonly state?: string;
    readonly zipCode?: string;
    readonly country?: Countries;
  };
}

export interface OrgAddressFormApi {
  readonly methods: UseFormReturn<FieldValues>;
  /** Resolves true only when the profile was actually written. */
  readonly submit: () => Promise<boolean>;
  /** Whether the user has changed anything. See `useSettledDirty`. */
  readonly isDirty: boolean;
  readonly reset: () => void;
  readonly language: unknown;
  readonly gridLayoutSettings: Record<string, boolean>;
  readonly mailingAddressFeatureOn: boolean;
  readonly isSameAddress: boolean;
  readonly disabledFields: string[];
  readonly areAnyPhysicalAddressFieldsEmpty: boolean;
  readonly handleSameAddress: () => void;
  readonly businessMissingCount: number;
  readonly mailingMissingCount: number;
}

/**
 * The Business Address and Mailing Address forms are one `useForm` -- the
 * "same as business address" sync writes across both -- lifted out of the old
 * `Contact` component so the page can own a single save bar.
 *
 * <p>Nothing here is feature-flag gated for editability. The old component
 * derived an `isDisabled` from `submissions_view` and rendered every field
 * read-only, which is FIR-21111. The `disabledFields` returned here are the
 * mailing-sync hook's own, which is form behaviour, not that lock.
 */
export const useOrgAddressForm = ({
  accountId,
  orgData,
}: {
  readonly accountId: string;
  readonly orgData: OrgAddressOrgData;
}): OrgAddressFormApi => {
  const notifications = useNotifications();
  const { gridLayoutSettings, ...languageFormState } = useLanguageFormState({
    language: COUNTRY_MAPPING[orgData.country as keyof typeof COUNTRY_MAPPING],
    options: { validatePhone: true },
  });
  const country = useCountryNameByPlatform();
  const mailingAddressFeatureOn = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SUBMISSIONS_MAILING_ADDRESS as never
  );

  const defaultValues = React.useMemo(
    () => ({
      state: orgData.state,
      address1: orgData.address1,
      address2: orgData.address2,
      city: orgData.city,
      zipCode: orgData.zipCode,
      phoneNumber: orgData.phoneNumber,
      // The MUI phone field derived this internally; a plain select needs it
      // in the form's defaults or the code renders blank on a saved number.
      phoneNumberCountryCode: defaultCountryCodeFor(
        languageFormState.language as Languages
      ),
      mailingAddress: {
        address1: orgData.mailingAddress?.address1 ?? '',
        address2: orgData.mailingAddress?.address2 ?? '',
        address3: orgData.mailingAddress?.address3 ?? '',
        city: orgData.mailingAddress?.city ?? '',
        state: orgData.mailingAddress?.state ?? '',
        zipCode: orgData.mailingAddress?.zipCode ?? '',
        country,
      },
    }),
    [orgData, country, languageFormState.language]
  );

  const mailingSchema = useMailingAddressValidationSchema(
    mailingAddressFeatureOn,
    languageFormState.languageFormSchema
  );

  const formMethods = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(mailingSchema),
  });

  const onSubmit = async (formValues: FieldValues): Promise<boolean> => {
    try {
      await languageFormState.validateAddress(formValues);
    } catch {
      return false;
    }

    try {
      await updateOrgProfile({}, { accountId, ...formValues });
    } catch (error) {
      notifications.error(manageAPIError(error as AxiosError));
      // Deliberately NOT resetting here. The component this replaced reset in
      // a `finally`, so a rejected save still marked the form clean; with a
      // single save bar keyed on `isDirty` that made the bar disappear and a
      // failed save look like a successful one. Staying dirty keeps the bar up
      // so the edit can be retried.
      return false;
    }

    notifications.success('Profile updated successfully');
    formMethods.reset(formValues);
    rebaseBaseline();
    return true;
  };

  const {
    isSameAddress,
    disabledFields,
    areAnyPhysicalAddressFieldsEmpty,
    handleSameAddress,
  } = useMailingAddressSync({ formMethods, country });

  React.useEffect(() => {
    handleEmptyMailingAddress(formMethods, country, handleSameAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const values = formMethods.watch();
  const { isDirty, rebaseBaseline } = useSettledDirty(
    values,
    formMethods.getValues
  );

  /** See the note on `useOrgDetailsForm.submit`. */
  const submit = async (): Promise<boolean> => {
    let saved = false;
    await formMethods.handleSubmit(async (formValues) => {
      saved = await onSubmit(formValues);
    })();
    return saved;
  };

  return {
    methods: formMethods,
    isDirty,
    submit,
    reset: () => {
      formMethods.reset(defaultValues);
      rebaseBaseline();
    },
    language: languageFormState.language,
    gridLayoutSettings,
    mailingAddressFeatureOn,
    isSameAddress,
    disabledFields,
    areAnyPhysicalAddressFieldsEmpty,
    handleSameAddress,
    businessMissingCount: countBusinessMissing(
      languageFormState.languageFormSchema,
      values
    ),
    mailingMissingCount: countMailingMissing(
      mailingSchema,
      values,
      isSameAddress
    ),
  };
};

/**
 * The physical address's required list comes from the per-country field map,
 * not from the form's resolver.
 *
 * <p>`useMailingAddressValidationSchema` returns an empty object schema when the
 * mailing feature is off, and only ever reads the country map for the mailing
 * postcode -- so the resolver knows nothing about address1/city/zipCode. The
 * country map is where their requiredness actually lives.
 */
function countBusinessMissing(
  languageFormSchema: Record<string, unknown> | undefined,
  values: FieldValues
): number {
  if (!languageFormSchema) {
    return 0;
  }

  const described = Yup.object()
    .shape(languageFormSchema as never)
    .describe().fields as Record<string, unknown>;

  return countMissingRequired(described, values);
}

/** Nothing is outstanding when the mailing address simply mirrors the business one. */
function countMailingMissing(
  mailingSchema: Yup.ObjectSchema,
  values: FieldValues,
  isSameAddress: boolean
): number {
  if (isSameAddress) {
    return 0;
  }

  const fields = mailingSchema.describe().fields as Record<string, any>;
  const mailingFields = fields?.mailingAddress?.fields as
    | Record<string, unknown>
    | undefined;

  if (!mailingFields) {
    return 0;
  }

  return countMissingRequired(mailingFields, values.mailingAddress ?? {});
}

/**
 * An account with no mailing address on file starts out mirroring the business
 * address rather than showing an empty second form.
 */

function handleEmptyMailingAddress(
  formMethods: UseFormReturn<FieldValues>,
  country: string,
  handleSameAddress: () => void
) {
  const mailingAddress = formMethods.getValues('mailingAddress');

  const isMailingAddressEmpty =
    !mailingAddress?.address1?.trim() &&
    !mailingAddress?.city?.trim() &&
    !mailingAddress?.zipCode?.trim() &&
    (!isUnitedStates(country) || !mailingAddress?.state?.trim());

  if (isMailingAddressEmpty) {
    handleSameAddress();
  }
}

export default useOrgAddressForm;
